import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import AnalyticsTracker from './components/AnalyticsTracker';
import './App.css';

// Suppress browser-internal errors globally
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    if (e.message && (
      e.message.includes('postMessage') || 
      e.message.includes('could not be cloned') ||
      e.message.includes('Request object') ||
      e.message.includes('ResizeObserver')
    )) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  }, true);
  
  window.addEventListener('unhandledrejection', (e) => {
    const msg = e.reason?.message || String(e.reason) || '';
    if (msg.includes('postMessage') || 
        msg.includes('could not be cloned') ||
        msg.includes('Request object')) {
      e.preventDefault();
      return false;
    }
  });
}

// Pages
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import Training from './pages/Training';
import Nutrition from './pages/Nutrition';
import Progress from './pages/Progress';
import Challenges from './pages/Challenges';
import Messages from './pages/Messages';
import Profile from './pages/Profile';
import Subscription from './pages/Subscription';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import AdminDashboard from './pages/AdminDashboard';
import About from './pages/About';
import Science from './pages/Science';
import EventTraining from './pages/EventTraining';
import Compare from './pages/Compare';
import WorkoutPrograms from './pages/WorkoutPrograms';
import MealPlans from './pages/MealPlans';
import Challenge from './pages/Challenge';

// Loading spinner component
const LoadingSpinner = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
  </div>
);

// Protected Route wrapper - requires authentication only
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  
  return children;
};

// Subscription Required Route - requires authentication + active subscription (including trial)
const SubscriptionRequiredRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  
  // Check if onboarding is completed
  if (!user?.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }
  
  // Check if user has active subscription (active or trialing)
  const hasActiveSubscription = user?.subscription_status === 'active' || user?.subscription_status === 'trialing';
  
  if (!hasActiveSubscription) {
    return <Navigate to="/subscription" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirect to appropriate page if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) return <LoadingSpinner />;
  
  if (isAuthenticated) {
    // Step 1: Complete onboarding first
    if (!user?.onboarding_completed) {
      return <Navigate to="/onboarding" replace />;
    }
    
    // Step 2: Must have subscription to access dashboard
    const hasActiveSubscription = user?.subscription_status === 'active' || user?.subscription_status === 'trialing';
    if (!hasActiveSubscription) {
      return <Navigate to="/subscription" replace />;
    }
    
    // Step 3: Has subscription, go to dashboard
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Auth /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Auth /></PublicRoute>} />
      <Route path="/about" element={<About />} />
      <Route path="/science" element={<Science />} />
      <Route path="/event-training" element={<EventTraining />} />
      <Route path="/compare" element={<Compare />} />
      <Route path="/programs" element={<WorkoutPrograms />} />
      <Route path="/programs/:type" element={<WorkoutPrograms />} />
      <Route path="/meal-plans" element={<MealPlans />} />
      <Route path="/meal-plans/:type" element={<MealPlans />} />
      <Route path="/challenge" element={<Challenge />} />
      
      {/* Admin route - separate authentication */}
      <Route path="/admin" element={<AdminDashboard />} />
      
      {/* Protected routes - need auth but not subscription */}
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/subscription/cancel" element={<ProtectedRoute><SubscriptionCancel /></ProtectedRoute>} />
      
      {/* Subscription required routes - need auth AND active subscription */}
      <Route path="/dashboard" element={<SubscriptionRequiredRoute><Dashboard /></SubscriptionRequiredRoute>} />
      <Route path="/training" element={<SubscriptionRequiredRoute><Training /></SubscriptionRequiredRoute>} />
      <Route path="/nutrition" element={<SubscriptionRequiredRoute><Nutrition /></SubscriptionRequiredRoute>} />
      <Route path="/progress" element={<SubscriptionRequiredRoute><Progress /></SubscriptionRequiredRoute>} />
      <Route path="/challenges" element={<SubscriptionRequiredRoute><Challenges /></SubscriptionRequiredRoute>} />
      <Route path="/messages" element={<SubscriptionRequiredRoute><Messages /></SubscriptionRequiredRoute>} />
      <Route path="/profile" element={<SubscriptionRequiredRoute><Profile /></SubscriptionRequiredRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <div className="App">
              <AnalyticsTracker />
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </div>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
